/**
 * Account-scoped custom COSHH substance — the wire shape returned by
 * `GET /api/mobile/v1/account/custom-coshh` and mutated by POST/PATCH on the
 * sibling routes. Mirrors Prisma `AccountCustomCoshh` (account-level catalogue
 * entries the user has added themselves, in addition to the global catalogue).
 *
 * Web parity:
 *   - GET/POST  → app/api/account-custom-coshh/route.ts
 *   - PATCH/DEL → app/api/account-custom-coshh/[coshhId]/route.ts
 *
 * These schemas are the SINGLE source of truth for both surfaces: the web
 * routes import them too (they previously carried their own hand-maintained
 * copies, which is how the mobile PATCH silently drifted to a 3-field subset
 * while the web one grew full SDS depth — #2274). Change a field here and both
 * routes move together; the row → wire mapping lives in
 * `lib/coshh/custom-coshh-fields.ts`.
 *
 * Routes are gated behind `FEATURE.COSHH` (lib/gating.ts) — the mobile route
 * returns `PLAN_LOCKED` (HTTP 403) when the caller's plan doesn't include it.
 */
import { z } from "zod";

/**
 * Date fields cross the wire as strings (`yyyy-mm-dd` from a date picker, or a
 * full ISO timestamp) and are converted to `Date` at the route boundary, so an
 * invalid value must be refused HERE — otherwise it either reaches Prisma as an
 * `Invalid Date` (500) or, worse, is silently accepted as some other date.
 *
 * `Date.parse` is the wrong test for that: it is deliberately lenient, so
 * `2026-02-30` normalises to 2 March and the UK-style `09/02/2026` parses as
 * 2 September. Both would persist a date the user never entered. These two
 * primitives are calendar-correct instead — `.date()` rejects 30 February and
 * a 29 February in a non-leap year, and `.datetime({ offset: true })` applies
 * the same calendar check to the date half while requiring `Z` or a `±HH:MM`
 * offset, so a timestamp can never be stored against an ambiguous zone.
 */
const CalendarDate = z.string().date();
const IsoTimestamp = z.string().datetime({ offset: true });

const IsoDateString = z
  .string()
  .refine(
    (v) => CalendarDate.safeParse(v).success || IsoTimestamp.safeParse(v).success,
    {
      message:
        "Expected a calendar date (YYYY-MM-DD) or an ISO-8601 timestamp with a UTC offset",
    },
  );

export const CustomCoshhSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    category: z.string(),
    welLimit: z.string().nullable(),
    healthEffects: z.string().nullable(),
    // Assessment depth — editable on both surfaces, rendered on the substance
    // detail view. Every column is nullable in Prisma.
    controlMeasures: z.string().nullable(),
    exposureRoutes: z.string().nullable(),
    ppeRequired: z.string().nullable(),
    firstAidMeasures: z.string().nullable(),
    // G5-1 SDS depth — populated by SDS extraction or a manual edit.
    manufacturer: z.string().nullable(),
    sdsVersion: z.string().nullable(),
    /** ISO timestamp (Prisma `DateTime?`), or null. */
    sdsIssueDate: z.string().nullable(),
    hazardStatements: z.string().nullable(),
    precautionaryStatements: z.string().nullable(),
    fireMeasures: z.string().nullable(),
    storage: z.string().nullable(),
    disposal: z.string().nullable(),
    /**
     * ISO timestamp for the substance's next SDS review, or null. Drives the
     * "SDS review overdue / due in Nd" badge on the web library — the mobile
     * screen needs the raw date to derive the same status.
     */
    sdsReviewDueAt: z.string().nullable(),
    /**
     * Private storage reference to the source SDS PDF, set when the substance was
     * created from an uploaded Safety Data Sheet. Re-opened via the presigned
     * `/account/custom-coshh/[coshhId]/sds` route — never rendered/opened raw.
     * Null for manually-added substances.
     */
    sdsFileUrl: z.string().nullable(),
  });
export type CustomCoshh = z.infer<typeof CustomCoshhSchema>;

/**
 * #2411 server-boundary variant. The READ shape above is what the mobile app
 * parses off the wire, so it is permissive — an installed build must tolerate
 * a field the server adds. The Create/Update REQUEST shapes below stay
 * `.strict()`: rejecting an unexpected key there costs no installed client
 * anything, and the server validates input it controls.
 */
export const CustomCoshhStrict = CustomCoshhSchema.strict();

/**
 * Body for POST. The server upserts on `(accountId, name)`; re-adding the same
 * name overwrites the existing row rather than failing.
 *
 * The base three fields (name/welLimit/healthEffects) power the manual "+ Add"
 * form. The remaining fields carry full G5-1 SDS depth + the relink id when the
 * substance is created from an uploaded Safety Data Sheet, mirroring the web
 * `POST /api/account-custom-coshh` body. All optional so the manual path is
 * unaffected.
 */
export const CustomCoshhCreateSchema = z
  .object({
    name: z.string().min(1).max(200),
    welLimit: z.string().max(200).optional(),
    healthEffects: z.string().max(1000).optional(),
    // SDS depth (optional; populated from a Safety Data Sheet extraction).
    category: z.string().max(100).optional(),
    controlMeasures: z.string().max(1000).optional(),
    exposureRoutes: z.string().max(1000).optional(),
    ppeRequired: z.string().max(1000).optional(),
    firstAidMeasures: z.string().max(1000).optional(),
    manufacturer: z.string().max(200).optional(),
    sdsVersion: z.string().max(100).optional(),
    sdsIssueDate: IsoDateString.optional(),
    hazardStatements: z.string().max(4000).optional(),
    precautionaryStatements: z.string().max(4000).optional(),
    fireMeasures: z.string().max(2000).optional(),
    storage: z.string().max(2000).optional(),
    disposal: z.string().max(2000).optional(),
    sdsReviewDueAt: IsoDateString.optional(),
    /**
     * Id of the UploadedSDS row produced by the SDS extract route. When present
     * AND that upload has a stored file, the server links the stored PDF onto
     * the substance (sdsFileUrl/uploadedSdsId) + back-links approvedSubstanceId.
     */
    uploadedSdsId: z.string().optional(),
  })
  .strict();
export type CustomCoshhCreate = z.infer<typeof CustomCoshhCreateSchema>;

/**
 * Body for PATCH — the full create field set, partial. Every field is optional
 * so PATCH stays a partial update, and everything except `name`/`category`
 * accepts `null` so the client can explicitly clear it.
 *
 * `category` is deliberately NOT nullable: the column is non-null with a
 * "Custom" default, so a null would 500 at the database. Omit it to leave the
 * current value alone (the web form does exactly that on a blank input).
 *
 * `uploadedSdsId` is create-only — an edit must never be able to re-point (or
 * clear) the stored SDS link. Web parity: the web PATCH schema omits it too.
 */
export const CustomCoshhUpdateSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    category: z.string().max(100).optional(),
    welLimit: z.string().max(200).nullable().optional(),
    healthEffects: z.string().max(1000).nullable().optional(),
    controlMeasures: z.string().max(1000).nullable().optional(),
    exposureRoutes: z.string().max(1000).nullable().optional(),
    ppeRequired: z.string().max(1000).nullable().optional(),
    firstAidMeasures: z.string().max(1000).nullable().optional(),
    manufacturer: z.string().max(200).nullable().optional(),
    sdsVersion: z.string().max(100).nullable().optional(),
    sdsIssueDate: IsoDateString.nullable().optional(),
    hazardStatements: z.string().max(4000).nullable().optional(),
    precautionaryStatements: z.string().max(4000).nullable().optional(),
    fireMeasures: z.string().max(2000).nullable().optional(),
    storage: z.string().max(2000).nullable().optional(),
    disposal: z.string().max(2000).nullable().optional(),
    sdsReviewDueAt: IsoDateString.nullable().optional(),
  })
  .strict();
export type CustomCoshhUpdate = z.infer<typeof CustomCoshhUpdateSchema>;
